import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTareas } from '../../context/TareasContext';
import { useAuth } from '../../context/AuthContext';
import { useConfirmar } from '../../context/ConfirmarContext';
import { useLimite } from '../../hooks/useLimite';
import { usePerfil } from '../../context/PerfilContext';
import { Casa } from '../../types';
import { colorDe, tareasQueSeRepiten, yoEn } from '../../utils/tareas';
import { LIMITES_GRATIS } from '../../config/planes';
import { ContadorLimite } from '../../components/plus/ContadorLimite';
import { ModalCasa } from '../../components/tareas/ModalCasa';
import { ModalPersonasCasa } from '../../components/tareas/ModalPersonasCasa';
import { ElegirParticipanteCasa } from '../../components/tareas/ElegirParticipanteCasa';
import { botonPrincipal, botonIcono } from '../../components/tareas/ui';
import { Crown, Edit2, Plus, Sparkles, Trash2, Users } from 'lucide-react';

/** Una casa con su gente, cuántas tareas tiene y qué puedes hacer con ella. */
const TarjetaCasa: React.FC<{ casa: Casa; onPersonas: () => void; onEditar: () => void }> =
  ({ casa, onPersonas, onEditar }) => {
  const { user } = useAuth();
  const { tareas, eliminarCasa, casaConPlus } = useTareas();
  const confirmar = useConfirmar();
  const { esPlus } = usePerfil();
  const organizo = casa.organizadoresUids.includes(user?.uid ?? '');
  const activas = tareas.filter(t => t.casaId === casa.id && t.activa).length;
  const repetidas = tareasQueSeRepiten(tareas, casa.id);
  const sinLimite = casaConPlus(casa.id);

  const borrar = async () => {
    if (!(await confirmar({
      titulo: `¿Borrar «${casa.nombre}»?`,
      mensaje: casa.personal
        ? 'Se borran tus tareas personales y su historial. Se volverá a crear vacía al entrar en la semana.'
        : 'Se borran sus tareas y su historial para todos los que viven en ella.',
      accion: 'Borrar', destructiva: true,
    }))) return;
    await eliminarCasa(casa.id).catch(e => console.error(e));
  };

  return (
    <div className="p-4 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
      <div className="flex items-start gap-3">
        <div className="w-12 h-12 shrink-0 rounded-xl bg-lumbre-50 dark:bg-lumbre-900/30 flex items-center justify-center text-2xl">
          {casa.icono}
        </div>
        <div className="min-w-0 flex-1">
          <h2 className="font-bold text-gray-900 dark:text-white truncate flex items-center gap-1.5">
            {casa.nombre}
            {organizo && !casa.personal && <Crown className="w-3.5 h-3.5 text-amber-500 shrink-0" aria-label="La organizas" />}
          </h2>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {casa.personal ? 'Solo tuya' : `${casa.participantes.filter(p => p.activo).length} personas`}
            {` · ${activas} ${activas === 1 ? 'tarea' : 'tareas'}`}
            {!sinLimite && ` · ${repetidas} de ${LIMITES_GRATIS.tareasPeriodicas} que se repiten`}
          </p>
          {!casa.personal && (
            <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2">
              {casa.participantes.filter(p => p.activo).map(p => (
                <span key={p.id} className="inline-flex items-center gap-1 text-xs text-gray-700 dark:text-gray-300">
                  <span className={`w-2.5 h-2.5 rounded-full ${colorDe(casa, p.id).punto}`} aria-hidden />
                  {p.nombre}
                </span>
              ))}
            </div>
          )}
        </div>
        <div className="flex shrink-0">
          {!casa.personal && (
            <button onClick={onPersonas} title="Quién vive aquí e invitar" className={botonIcono}><Users className="w-4 h-4" /></button>
          )}
          {organizo && <button onClick={onEditar} title="Cambiar nombre o icono" className={botonIcono}><Edit2 className="w-4 h-4" /></button>}
          {organizo && (
            <button onClick={borrar} title="Borrar la casa"
              className="p-2 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition">
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
      {!casa.personal && !esPlus && casa.plusOrganizador && (
        <p className="mt-2 text-[11px] text-amber-700 dark:text-amber-300 inline-flex items-center gap-1">
          <Sparkles className="w-3 h-3" /> Quien organiza tiene Plus: esta casa va sin límites para todos.
        </p>
      )}
    </div>
  );
};

/** Las casas del usuario: la personal («Mis tareas») y las compartidas. */
export const Casas: React.FC = () => {
  const { user } = useAuth();
  const { casas, cargando } = useTareas();
  const limite = useLimite('casasCompartidas');
  const [nueva, setNueva] = useState(false);
  const [editando, setEditando] = useState<Casa | null>(null);
  const [personasDe, setPersonasDe] = useState<string | null>(null);
  const casaPersonas = casas.find(c => c.id === personasDe);

  if (cargando && casas.length === 0) {
    return <div className="max-w-3xl mx-auto px-4 py-10 text-gray-500 dark:text-gray-400">Cargando…</div>;
  }

  const compartidas = casas.filter(c => !c.personal);

  return (
    <div className="max-w-3xl mx-auto px-4 py-6">
      <header className="flex flex-wrap items-start justify-between gap-3 mb-5">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Casas</h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">
            Con quién compartes las tareas.{' '}
            <Link to="/ayuda#tareas-casa" className="font-semibold text-lumbre-700 dark:text-lumbre-400 hover:underline">
              ¿Cómo funciona?
            </Link>
          </p>
        </div>
        <button onClick={() => limite.intentar(() => setNueva(true))} className={botonPrincipal}>
          <Plus className="w-4 h-4" /> Nueva casa <ContadorLimite texto={limite.texto} />
        </button>
      </header>

      <div className="space-y-3">
        {casas.filter(c => !c.personal && !yoEn(c, user?.uid)).map(c => <ElegirParticipanteCasa key={c.id} casa={c} />)}
        {casas.map(c => (
          <TarjetaCasa key={c.id} casa={c} onPersonas={() => setPersonasDe(c.id)} onEditar={() => setEditando(c)} />
        ))}
      </div>

      {compartidas.length === 0 && (
        <div className="mt-4 text-center py-10 bg-white dark:bg-gray-800 rounded-xl border border-dashed border-gray-300 dark:border-gray-700">
          <Users className="w-10 h-10 mx-auto text-gray-300 dark:text-gray-600 mb-3" />
          <p className="text-gray-700 dark:text-gray-300 font-medium">¿Vives con alguien?</p>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 max-w-sm mx-auto">
            Crea vuestra casa, apunta a quien vive en ella e invítales con un enlace. Veréis todos la misma semana y
            sabréis a quién le toca cada cosa.
          </p>
          <button onClick={() => limite.intentar(() => setNueva(true))} className={`mt-4 ${botonPrincipal}`}>
            <Plus className="w-4 h-4" /> Crear una casa
          </button>
        </div>
      )}

      {nueva && <ModalCasa onClose={() => setNueva(false)} onCreada={id => setPersonasDe(id)} />}
      {editando && <ModalCasa casa={editando} onClose={() => setEditando(null)} />}
      {casaPersonas && (
        <ModalPersonasCasa casa={casaPersonas} onClose={() => setPersonasDe(null)} onSalir={() => setPersonasDe(null)} />
      )}
    </div>
  );
};
